import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useProfile } from "@/hooks/use-profile";
import { cn } from "@/lib/utils";
import {
  menuPanelClass,
  menuRowClass,
  menuRowIconClass,
  menuSeparatorClass,
  sidebarFooterTileClass,
} from "@/lib/card-styles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BookMarked, BookOpen, ChevronDown, Compass, LogOut, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { KeyboardShortcutsHelp } from "@/components/command-palette/KeyboardShortcutsHelp";
import { signOut } from "@lyder-talk/core";
import { ProfileModal } from "@/components/profile/ProfileModal";
import { usePageTourContext } from "@/contexts/PageTourContext";
import { useTranslation } from "react-i18next";

import { ProfileDeviceControls } from "./profile-menu/ProfileDeviceControls";
import { ProfileIdentityRow } from "./profile-menu/ProfileIdentityRow";
import { ProfileStreakBand } from "./profile-menu/ProfileStreakBand";
import type { ProfileMenuGamificationProps, ProfileMenuThemeProps } from "./profile-menu/types";
import { auth } from "@lyder-talk/core/lib/auth-client";

export type { ProfileMenuGamificationProps, ProfileMenuThemeProps };
/** @deprecated Import `ProfileMenuGamificationProps` from `./profile-menu/types`. */
export type AvatarMenuGamificationProps = ProfileMenuGamificationProps;
/** @deprecated Import `ProfileMenuThemeProps` from `./profile-menu/types`. */
export type AvatarMenuThemeProps = ProfileMenuThemeProps;

interface AvatarMenuProps {
  gamificationProps?: ProfileMenuGamificationProps;
  themeProps?: ProfileMenuThemeProps;
  onStreakClick?: () => void;
  /** Fill the available width and label the trigger with the learner's identity. */
  stretch?: boolean;
}

/**
 * The profile dropdown — the account menu hung off the avatar in the global
 * rail, the mobile header, and the My Topics shell.
 *
 * ## Composition
 *
 * It follows the same page contract as every other screen, compressed to fit a
 * 288px panel:
 *
 *   1. who you are + the way into the profile modal
 *   2. ONE context strip — `ProfileStreakBand` (streak · level · XP)
 *   3. the menu rows
 *   4. device controls, then sign-out
 *
 * Two things were rebuilt rather than restyled. The identity header was a
 * `DropdownMenuLabel` carrying an `onClick` — a heading that behaved like a
 * button with nothing on screen saying so; it is now a real row
 * (`ProfileIdentityRow`). And the gamification block was a bare `<button>`
 * stranded between two separators with no framing; it is now the band's
 * component (`ProfileStreakBand`), a `slateSurfaceClass` tile whose single
 * target opens the streak detail.
 */
const AvatarMenu = ({
  gamificationProps,
  themeProps,
  onStreakClick,
  stretch = false,
}: AvatarMenuProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [userEmail, setUserEmail] = useState<string>("");
  const [userId, setUserId] = useState<string | null>(null);
  const { profile } = useProfile();
  const [profileOpen, setProfileOpen] = useState(false);
  const { startCurrentPageTour } = usePageTourContext();

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await auth.getSession();
      if (session?.user) {
        setUserEmail(session.user.email || "");
        setUserId(session.user.id);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUserEmail(session.user.email || "");
        setUserId(session.user.id);
      } else {
        setUserEmail("");
        setUserId(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      // Always navigate and show toast, even if signOut failed
      navigate("/");
      toast({
        title: t("nav.avatar.signedOutTitle", "Signed out"),
        description: t("nav.avatar.signedOutDescription", "You have been signed out successfully."),
      });
    }
  };

  if (!userEmail) {
    return null;
  }

  const displayName = profile?.display_name?.trim() || "";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            title={stretch ? `${displayName || userEmail} · ${userEmail}` : undefined}
            className={cn(
              "relative flex items-center gap-2 outline-none",
              stretch
                ? cn(
                    sidebarFooterTileClass,
                    "h-11 w-full min-w-0 justify-start px-1.5 text-left",
                    // The chevron is the affordance that this tile opens a panel —
                    // without it the sidebar footer read as a static identity chip.
                    "[&[data-state=open]_.js-avatar-chevron]:rotate-180",
                  )
                : "rounded-full transition-colors",
            )}
          >
            <span className="relative flex shrink-0 items-center">
              <UserAvatar
                userId={userId}
                displayName={profile?.display_name}
                email={userEmail}
                avatarUrl={profile?.avatar_url}
                size="lg"
                showBorder={true}
                fallbackClassName="bg-primary-strong text-white font-semibold"
              />
            </span>
            {stretch && (
              <>
                <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium leading-tight">
                  {displayName || userEmail}
                </span>
                <ChevronDown
                  className="js-avatar-chevron h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform duration-200 dark:text-stone-500"
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className={cn(menuPanelClass, "w-72")}>
          <ProfileIdentityRow
            userId={userId}
            displayName={profile?.display_name}
            email={userEmail}
            avatarUrl={profile?.avatar_url}
            onClick={() => setProfileOpen(true)}
            ariaLabel={t("nav.avatar.openProfile", "Open profile settings")}
          />

          <div className={menuSeparatorClass} role="separator" />

          {gamificationProps && !gamificationProps.isLoading && (
            <ProfileStreakBand
              gamification={gamificationProps}
              onOpen={onStreakClick}
              className="my-0.5"
            />
          )}

          <div className={menuSeparatorClass} role="separator" />

          <DropdownMenuItem onSelect={() => setProfileOpen(true)} className={cn(menuRowClass)}>
            <span className={cn(menuRowIconClass, "bg-primary/10 text-primary")}>
              <User className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("nav.avatar.profile", "Profile")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/my-topics")} className={cn(menuRowClass)}>
            <span className={cn(menuRowIconClass, "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300")}>
              <BookOpen className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("nav.avatar.myTopics", "My Topics")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/vocabulary")} className={cn(menuRowClass)}>
            <span className={cn(menuRowIconClass, "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300")}>
              <BookMarked className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("nav.avatar.myVocabulary", "My Vocabulary")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={startCurrentPageTour} className={cn(menuRowClass)}>
            <span className={cn(menuRowIconClass, "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300")}>
              <Compass className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("nav.avatar.takeTour", "Take a tour")}
          </DropdownMenuItem>

          <div className={menuSeparatorClass} role="separator" />

          <KeyboardShortcutsHelp
            asMenuItem
            className={cn(
              menuRowClass,
              "[&>svg]:text-stone-500 [&>svg]:dark:text-stone-400",
            )}
          />

          <div className={menuSeparatorClass} role="separator" />

          <ProfileDeviceControls themeProps={themeProps} />

          <div className={menuSeparatorClass} role="separator" />

          <DropdownMenuItem onSelect={handleSignOut} className={cn(menuRowClass, "text-destructive hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/10 dark:hover:text-destructive")}>
            <span className={cn(menuRowIconClass, "bg-destructive/10 text-destructive")}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </span>
            {t("nav.avatar.signOut", "Sign Out")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
};

export default AvatarMenu;